import { useEffect, useState } from "react";
import API from "services/axios";
import authHeader from "services/auth-header";

/**
 * Work order statuses come from Settings -> WO Statuses (user, 2026-10-29):
 * [{ id, value, name, color, sortId, lockCustomerApproval }] in sortId order.
 *
 *   value  what WorkOrders.WOStatus stores; never changes after the status is
 *          added, so renaming a status renames every tab and dropdown entry.
 *   name   the label to show.
 *
 * They feed every WO status dropdown (Approvals too), the W Orders tabs
 * (All + this list) and the Job Board tabs. One request per login, shared by
 * every screen; Settings calls loadWoStatuses(true) after a change.
 */

let cache = null; // { token, list }
let inflight = null;
const listeners = new Set();

const tokenNow = () => authHeader()["x-access-token"] || "";

export const loadWoStatuses = (force = false) => {
  const token = tokenNow();
  if (!force && cache?.token === token) return Promise.resolve(cache.list);
  if (!force && inflight?.token === token) return inflight.promise;
  const promise = API.post("/settings/wo-statuses", {}, { headers: authHeader() })
    .then(({ data }) => {
      const list = Array.isArray(data?.data) ? data.data : [];
      cache = { token, list };
      listeners.forEach((fn) => fn(list));
      return list;
    })
    .finally(() => {
      if (inflight?.promise === promise) inflight = null;
    });
  inflight = { token, promise };
  return promise;
};

export const useWoStatuses = () => {
  const [list, setList] = useState(cache?.token === tokenNow() ? cache.list : []);
  useEffect(() => {
    listeners.add(setList);
    loadWoStatuses()
      .then(setList)
      .catch(() => {});
    return () => listeners.delete(setList);
  }, []);
  return list;
};

export const woStatusOf = (list, value) =>
  (list || []).find((s) => s.value === String(value || "").toLowerCase());

export const woStatusLabel = (list, value) =>
  woStatusOf(list, value)?.name || value || "";

// Dark text on light colours, white on dark ones.
export const textColorOn = (hex) => {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || "");
  if (!m) return "#fff";
  const n = parseInt(m[1], 16);
  const [r, g, b] = [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  return 0.299 * r + 0.587 * g + 0.114 * b > 170 ? "#212529" : "#fff";
};

// Inline style for a status badge / button in the status' own colour.
export const woStatusStyle = (list, value) => {
  const bg = woStatusOf(list, value)?.color || "#6c757d";
  return { backgroundColor: bg, borderColor: bg, color: textColorOn(bg) };
};
